import type { PanelFilterState } from '@features/instructors/contexts/filter/InstructorsFilterContext.types';

import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useOrganizations } from '@contexts/organizations-provider';
import { arePanelFiltersEqual } from '@features/instructors/contexts/filter/InstructorsFilterProvider.utils';
import { useInstructorsFilter } from '@features/instructors/contexts/filter/useInstructorsFilter';
import { CheckboxFilterSection } from '@shared/components/checkbox-filter-section';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { PanelLoadingState } from '@shared/components/panel-loading-state/PanelLoadingState';
import * as React from 'react';

import type {
  InstructorsFilterPanelProps,
  InstructorsFilterPanelSection,
} from './InstructorsFilterPanel.types';

import { useInstructorsFilterOptions } from '../../hooks/use-instructors-filter-options/useInstructorsFilterOptions';
import styles from './InstructorsFilterPanel.module.css';

const InstructorsFilterPanel = React.memo(
  ({ onClose }: Readonly<InstructorsFilterPanelProps>): React.ReactElement => {
    const isMobile = useMediaQuery(MediaQuery.Mobile);
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const { selectedOrganization } = useOrganizations();
    const { filters, setFilters } = useInstructorsFilter();

    const {
      data: filterOptions,
      isLoading,
      isError,
    } = useInstructorsFilterOptions(selectedOrganization?.customerId ?? '');

    const [draftFilters, setDraftFilters] = React.useState<PanelFilterState>({
      disciplines: filters.disciplines,
      programs: filters.programs,
      locations: filters.locations,
    });

    const isFiltersDirty = React.useMemo(
      () => !arePanelFiltersEqual(draftFilters, filters),
      [draftFilters, filters]
    );

    const isClearDisabled = React.useMemo(
      () =>
        draftFilters.disciplines.length === 0 &&
        draftFilters.programs.length === 0 &&
        draftFilters.locations.length === 0 &&
        filters.disciplines.length === 0 &&
        filters.programs.length === 0 &&
        filters.locations.length === 0,
      [draftFilters, filters]
    );

    const handleDisciplinesSelect = React.useCallback((id: string): void => {
      setDraftFilters(prev => ({
        ...prev,
        disciplines: prev.disciplines.includes(id)
          ? prev.disciplines.filter(item => item !== id)
          : [...prev.disciplines, id],
      }));
    }, []);

    const handleProgramsSelect = React.useCallback((id: string): void => {
      setDraftFilters(prev => ({
        ...prev,
        programs: prev.programs.includes(id)
          ? prev.programs.filter(item => item !== id)
          : [...prev.programs, id],
      }));
    }, []);

    const handleLocationsSelect = React.useCallback((id: string): void => {
      setDraftFilters(prev => ({
        ...prev,
        locations: prev.locations.includes(id)
          ? prev.locations.filter(item => item !== id)
          : [...prev.locations, id],
      }));
    }, []);

    const handleApply = React.useCallback((): void => {
      setFilters({
        disciplines: draftFilters.disciplines,
        programs: draftFilters.programs,
        locations: draftFilters.locations,
      });
      onClose();
    }, [setFilters, draftFilters, onClose]);

    const handleClear = React.useCallback((): void => {
      setFilters({
        disciplines: [],
        programs: [],
        locations: [],
      });
      onClose();
    }, [setFilters, onClose]);

    const isAllFiltersSelected = React.useMemo(() => {
      const disciplines = filterOptions?.disciplines ?? [];
      const programs = filterOptions?.programs ?? [];
      const locations = filterOptions?.locations ?? [];

      const totalOptions =
        disciplines.length + programs.length + locations.length;
      if (totalOptions === 0) return false;

      return (
        disciplines.every(opt =>
          draftFilters.disciplines.includes(opt.value)
        ) &&
        programs.every(opt => draftFilters.programs.includes(opt.value)) &&
        locations.every(opt => draftFilters.locations.includes(opt.value))
      );
    }, [filterOptions, draftFilters]);

    const hasFilterOptions = React.useMemo(
      () =>
        (filterOptions?.disciplines?.length ?? 0) > 0 ||
        (filterOptions?.programs?.length ?? 0) > 0 ||
        (filterOptions?.locations?.length ?? 0) > 0,
      [filterOptions]
    );

    const checkboxSections: InstructorsFilterPanelSection[] = React.useMemo(
      () => [
        {
          label: 'Not approved for',
          headingId: 'not-approved-for',
          dataTestId: 'not-approved-for-filter',
          filterKey: 'disciplines',
          options: (filterOptions?.disciplines ?? []).map(opt => ({
            name: opt.name,
            id: opt.value,
          })),
          onSelect: handleDisciplinesSelect,
        },
        {
          label: 'Program',
          headingId: 'program',
          dataTestId: 'program-filter',
          filterKey: 'programs',
          options: (filterOptions?.programs ?? []).map(opt => ({
            name: opt.name,
            id: opt.value,
          })),
          onSelect: handleProgramsSelect,
        },
        {
          label: 'Location',
          headingId: 'location',
          dataTestId: 'location-filter',
          filterKey: 'locations',
          options: (filterOptions?.locations ?? []).map(opt => ({
            name: opt.name,
            id: opt.value,
          })),
          onSelect: handleLocationsSelect,
        },
      ],
      [
        filterOptions,
        handleDisciplinesSelect,
        handleProgramsSelect,
        handleLocationsSelect,
      ]
    );

    return (
      <CobaltSidePanel
        isOpen
        showCloseButton={true}
        size={isMobileOrTablet ? 'large' : 'small'}
        title="Filters"
        onClose={onClose}
        actionBar={
          <CobaltActionBar
            primaryButton={
              <CobaltButton
                variant="ghost"
                disabled={isClearDisabled}
                onClick={handleClear}
              >
                Clear
              </CobaltButton>
            }
            tertiaryButton={
              <CobaltButton
                disabled={!isFiltersDirty || isAllFiltersSelected}
                onClick={handleApply}
              >
                Apply
              </CobaltButton>
            }
            variant="floating"
            mobile={isMobile}
          />
        }
      >
        <div className={styles.filtersPanelContent}>
          {isLoading && <PanelLoadingState />}
          {isError && <InlineFeedback type="data-error" />}
          {filterOptions && !hasFilterOptions && (
            <InlineFeedback type="so-empty" />
          )}
          {filterOptions &&
            hasFilterOptions &&
            checkboxSections.map((section, index) => (
              <div
                key={section.headingId}
                className={styles.checkboxSectionWrapper}
              >
                <CheckboxFilterSection
                  label={section.label}
                  headingId={section.headingId}
                  dataTestId={section.dataTestId}
                  options={section.options}
                  selected={draftFilters[section.filterKey]}
                  onSelect={section.onSelect}
                  searchable
                  hasDivider={index !== 0}
                />
              </div>
            ))}
        </div>
      </CobaltSidePanel>
    );
  }
);

InstructorsFilterPanel.displayName = 'InstructorsFilterPanel';

export { InstructorsFilterPanel };
